import { Component, Ref } from '@eastsideco/salvo-ts';
import { ThemeComponent } from "@/theme/ThemeComponent";
import { timeStamp } from 'console';

interface CountryData extends HTMLElement {
    /* eslint-disable */
    detected_values: {
        country: {
            name: string;
            handle: string;
        };
    };
    /* eslint-enable */
}

@Component export class GeoIp extends ThemeComponent {
    @Ref() closeTrigger!: HTMLButtonElement;
    @Ref() storeLeave!: HTMLAnchorElement;
    @Ref() storeRemain!: HTMLButtonElement;
    @Ref() storeHeader!: HTMLHeadingElement;
    @Ref() geoIpOverlay!: HTMLDivElement;
    @Ref() countryContainer!: HTMLDivElement;

    private contextUrl: string = 'browsing_context_suggestions.json';
    private userCountry: string = '';
    private altenateStores: string = '';
    private storeLocation: string = '';
    private preferenceDateTime: Date = this.getCurrentDate();

    private userPreferentialStoreUrl: string = localStorage.getItem('user_pref_store_url') || '';
    private userPreferentialStoreCode: string = localStorage.getItem('user_pref_store_code') || '';
    private userPreferentialStoreTimeout: string = localStorage.getItem('user_pref_store_timeout') || '';

    private merchantPreferentialStoreUrl: string = localStorage.getItem('merchant_pref_store_url') || '';
    private merchantPreferentialStoreCode: string = localStorage.getItem('merchant_pref_store_code') || '';
    private merchantPreferentialStoreTimeout: string = localStorage.getItem('merchant_pref_store_timeout') || '';

    // Remains false until a matching store has been found - prevents modal from showing
    private geoActive: boolean = false;

    /**
        * Initialise.
    **/
    init() {
        this.altenateStores = this.countryContainer.dataset.stores || '{}';
        this.storeLocation = this.countryContainer.dataset.location || '';
        this.findUserCountry();
        this.$listen(this.closeTrigger, 'click', () => this.closeSwitcher());
        this.$listen(this.geoIpOverlay, 'click', () => this.closeSwitcher());
        this.$listen(this.storeRemain, 'click', (e) => this.closeSwitcher());
        this.$listen(this.storeLeave, 'click', (e) => this.setPreferentialStore(e));
    }

    /**
        * Show / Hide Modal
        * geoActive checks that a valid store is available for the users country
        * storeLocation checks that the users country is different to the current store location
        * once both checks pass the modal appears
    **/
    toggleSwitcher() {
        const timestamp = this.getCurrentDate();

        /**
             * Parse localStorage date so it can be compared against
             * Get current date + time and put into same format as localStorage date
             * Compare the current datetime against the localStorage date
        */
        if (this.userPreferentialStoreUrl) {
            this.preferenceDateTime = new Date(this.userPreferentialStoreTimeout);
        } else if (this.merchantPreferentialStoreUrl) {
            this.preferenceDateTime = new Date(this.merchantPreferentialStoreTimeout);
        }

        if (timestamp < this.preferenceDateTime) {
            if (this.userPreferentialStoreCode && (window.location.host !== this.userPreferentialStoreUrl)) {
                window.location.href = this.userPreferentialStoreUrl;
                return;
            } else if (this.userCountry == this.merchantPreferentialStoreCode) {
                window.location.href = this.merchantPreferentialStoreUrl;
                return;
            }

        }

        const storeLocation = this.countryContainer.dataset.location;
        if (window.location.host == this.userPreferentialStoreUrl) {return;}
        if (!this.geoActive) { return;}
        if (storeLocation && storeLocation.includes(this.userCountry)) {return;}

        this.countryContainer.classList.remove('visually-hidden');
        this.countryContainer.classList.add('is-active');
        this.geoIpOverlay.classList.remove('visually-hidden');
        document.body.classList.add('drawer-open');
    }

    /**
        * Hide Modal
    **/
    closeSwitcher() {
        this.countryContainer.classList.add('visually-hidden');
        this.countryContainer.classList.remove('is-active');
        this.geoIpOverlay.classList.add('visually-hidden');
        document.body.classList.remove('drawer-open');
        const timeout = 30;
        const newTimestamp = this.getCurrentDate(timeout);
        localStorage.setItem('user_pref_store_url', window.location.host);
        localStorage.setItem('user_pref_store_code', this.storeLocation);
        localStorage.setItem('user_pref_store_timeout', `${newTimestamp}`);
    }

    /**
        * Find Users Country
        * Shopify Plus stores have a Geolocation app
        * Uses the browsing context suggestions to provide the users country details
    **/
    findUserCountry() {
        fetch(this.contextUrl, {
            method: 'get'
        })
            .then((response) => response.text())
            .then((text) => {
                const userData = JSON.parse(text);
                this.checkMerchantPreference(userData);
                this.recommendStore(userData);
            }).catch((event: unknown) => {
                if (event instanceof Error) {
                    this.$log.error(event.message);
                }
            });
    }

    checkMerchantPreference(countryData: CountryData) {
        if (!this.altenateStores) {return;}

        this.userCountry = this.handleize(countryData.detected_values.country.handle) || '';
        const parsedData = JSON.parse(this.altenateStores);

        for (const store of parsedData) {
            if (
                store.preferential &&
                store.preferential.enabled
            ) {
                const newTimestamp = this.getCurrentDate(store.preferential.timeout);

                localStorage.setItem('merchant_pref_store_url', store.url);
                localStorage.setItem('merchant_pref_store_code', store.code);
                localStorage.setItem('merchant_pref_store_timeout', `${newTimestamp}`);

                this.merchantPreferentialStoreUrl = localStorage.getItem('merchant_pref_store_url') || '';
                this.merchantPreferentialStoreCode = localStorage.getItem('merchant_pref_store_code') || '';
                this.merchantPreferentialStoreTimeout = localStorage.getItem('merchant_pref_store_timeout') || '';
                break;
            }
        }

        /**
            * Cleaning up localStorage prior to this.toggleSwitcher()
            * countryCount will increase if country isn't set as a preferential store
            * Compared against the full country list total
            * If the two values match it will remove the localStorage timestamp
            * so the GEO IP modal can open when other criteria is met
        */
        const preferenceDateTime = new Date(this.merchantPreferentialStoreTimeout);
        const timestamp = this.getCurrentDate();

        if (timestamp > preferenceDateTime) {
            localStorage.removeItem('user_pref_store_url');
            localStorage.removeItem('user_pref_store_code');
            localStorage.removeItem('user_pref_store_timeout');
        }
    }

    /**
        * Find a recommended store
        * Data is passed in, coming from the manual switcher section / blocks
        * This is used to update the CTA with the recommended store
        * geoActive is set to true as all required checks pass to show modal
        *
        * Update the modal header with the users current country
    **/
    recommendStore(countryData: CountryData) {
        if (!this.altenateStores) {return;}

        this.userCountry = this.handleize(countryData.detected_values.country.handle) || '';
        const parsedData = JSON.parse(this.altenateStores);
        let countryCount = 0;
        let leaveString = '';

        for (const store of parsedData) {
            const storeName = store.name.alternative ? store.name.alternative : store.name.default;

            if (this.userCountry.includes(store.code)) {
                if (
                    this.storeLeave &&
                    this.storeLeave.dataset &&
                    this.storeLeave.dataset.string
                ) {
                    leaveString = this.storeLeave.dataset.string.replace(
                        '{{ country }}',
                        storeName
                    );

                    this.storeLeave.innerHTML = this.decode(store.icon) + leaveString;
                    this.storeLeave.href = store.url;
                }

                if (
                    this.storeHeader &&
                    this.storeHeader.dataset &&
                    this.storeHeader.dataset.string
                ) {
                    const headerString = this.storeHeader.dataset.string.replace(
                        '{{ country }}',
                        storeName
                    );

                    this.storeHeader.innerHTML = headerString;
                }

                this.geoActive = true;
            }

            if (store.preferential && !store.preferential.enabled) {
                countryCount++;
            }
        }

        /**
            * Cleaning up localStorage prior to this.toggleSwitcher()
            * countryCount will increase if country isn't set as a preferential store
            * Compared against the full country list total
            * If the two values match it will remove the localStorage timestamp
            * so the GEO IP modal can open when other criteria is met
        */
        const preferenceDateTime = new Date(this.userPreferentialStoreTimeout);
        const timestamp = this.getCurrentDate();

        if (
            countryCount == parsedData.length ||
            timestamp > preferenceDateTime
        ) {
            localStorage.removeItem('user_pref_store_url');
            localStorage.removeItem('user_pref_store_code');
            localStorage.removeItem('user_pref_store_timeout');
        }

        this.toggleSwitcher();
    }

    /**
        * Set preferential store in localStorage
    **/
    setPreferentialStore(e: MouseEvent) {
        if (!this.altenateStores) { return; }
        e.preventDefault();

        const parsedData = JSON.parse(this.altenateStores);
        for (const store of parsedData) {
            if (
                this.userCountry.includes(store.code) &&
                this.storeLeave &&
                this.storeLeave.dataset &&
                this.storeLeave.dataset.string &&
                store.preferential
            ) {
                const timeout = store.preferential.timeout || '30';
                const newTimestamp = this.getCurrentDate(timeout);

                localStorage.setItem('user_pref_store_url', store.url);
                localStorage.setItem('user_pref_store_code', store.code);
                localStorage.setItem('user_pref_store_timeout', `${newTimestamp}`);
            }
        }

        window.location.href = this.storeLeave.href;
    }

    /**
        * Decode HTML
        * Mainly used for the SVG icons when updating the CTAs
    **/
    decode(string: string) {
        const text = new DOMParser().parseFromString(string, 'text/html');
        return text.documentElement.textContent || '';
    }

    /**
        * Get the current date with specific format
        * @param hours - increase returned date by x hours
    **/
    getCurrentDate(hours?: number) {
        const currentDate = new Date();

        if (hours) {
            currentDate.setHours(currentDate.getHours() + Number(hours));
        }

        currentDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        currentDate.toLocaleDateString("en-GB", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });

        return currentDate;
    }

    /**
        * Handleize
        * Works similar to "| handleize" Shopify filter
        * Used for formatting country names when comparing against store country
    **/
    handleize(string: string) {
        if (!string) { return; }
        return string.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '').replace(/^-/, '');
    }
}
